import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { ROUTES, isAuthRoute } from "./routes";
import { getDefaultHomeRoute, getClientHomeRoute, isEngagementsOnlyClient } from "./utils/clientMsp";

// AUTH PAGES
import Login from "./auth/Login";
import AzureCallback from "./auth/AzureCallback";
import ForgotPassword from "./auth/ForgotPassword";
import ResetPassword from "./auth/ResetPassword";

// SUPERADMIN PAGES
import Overview from "./pages/Superadmin/Overview";
import SecurityOverview from "./pages/Security/SecurityOverview";
import AssetsOverview from "./pages/Assets/AssetsOverview";
import NetworkOverview from "./pages/Network/NetworkOverview";
import EngagementPage from "./pages/Engagement/EngagementPage";

// CLIENT PAGE
import ClientOverview from "./pages/client/Overview";
import HelpdeskPage from "./pages/client/Helpdesk";

// OTHER PAGES
import NotificationsPage from "./pages/Notifications/NotificationsPage";
import UserProfilePage from "./pages/profile/userProfile";

// LAYOUT
import Layout from "./components/layout/Layout";
import { DataCacheProvider } from "./contexts/DataCacheContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DateFilterProvider } from "./contexts/DateFilterContext";
import { fetchClients } from "./api/overviewApi";

// STYLES
import "./styles/custom.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/theme.css";

/* -------------------------------------------------------------------------- */
/*                                WRAPPER LOGIC                               */
/* -------------------------------------------------------------------------- */

// Protected Route Component
function ProtectedRoute({ children, requireSuperadmin = false }) {
  const { isAuthenticated, loading, isStaffUser, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>; // You can replace this with a loading spinner
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  if (requireSuperadmin && !isStaffUser()) {
      return <Navigate to={getDefaultHomeRoute(user)} replace />;
  }

  return children;
}

function RestrictedClientRoute({ client, children }) {
  const location = useLocation();
  const { isStaffUser } = useAuth();

  if (!isEngagementsOnlyClient(client)) {
    return children;
  }

  const isClientView = location.pathname.startsWith('/client');
  const homeRoute = getClientHomeRoute(client, {
    isStaff: isStaffUser(),
    isClientView,
  });

  return <Navigate to={homeRoute} replace />;
}

function AppWrapper() {
  const location = useLocation();
  const hideLayout = isAuthRoute(location.pathname);

  return <App hideLayout={hideLayout} />;
}

function App({ hideLayout }) {
  const { user, isStaffUser } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);

  const hydrateClient = (client) => {
    if (!client) return client;
    return {
      ...client,
      hasHelpdesk: Boolean(
        client.hasHelpdesk ||
        client.has_freshservice ||
        client.freshservice_workspace_id
      ),
    };
  };

  // The login response (/api/auth/user/, /api/auth/azure-verify/) already carries the
  // freshservice flags for the user's own client, so helpdesk nav state is known
  // immediately — no need to wait on a separate client-list fetch to find out.
  const clientFromUser = (u) => {
    if (!u?.client) return null;
    return hydrateClient({
      client_id: u.client.client_id,
      client_name: u.client.client_name,
      has_autotask: u.client.has_autotask,
      has_freshservice: u.client.has_freshservice,
      freshservice_workspace_id: u.client.freshservice_workspace_id,
      has_noc: u.client.has_noc,
      has_soc: u.client.has_soc,
      is_msp_client: u.client.is_msp_client,
    });
  };

  const [selectedClient, setSelectedClient] = useState(() => {
    if (user && !user.is_staff) {
      return clientFromUser(user);
    }
    return null;
  });

  // Allow any child component to open the notifications panel via a custom event
  React.useEffect(() => {
    const handler = () => setShowNotifications(true);
    window.addEventListener('open-notifications', handler);
    return () => window.removeEventListener('open-notifications', handler);
  }, []);

  // Set initial client for normal users and hydrate the rest of the client record
  // (territory, category, etc.) from the backend once the full list loads.
  React.useEffect(() => {
    if (!user || isStaffUser()) return;

    const clientId = user.client?.client_id || user.client_id;
    if (!clientId) return;

    // Already have this client with freshservice metadata resolved (from login or a
    // prior fetch) — nothing to hydrate.
    if (selectedClient && selectedClient.client_id === clientId && selectedClient.has_autotask !== undefined) {
      return;
    }

    let cancelled = false;

    const loadClient = async () => {
      const fallback = clientFromUser(user) || hydrateClient({
        client_id: clientId,
        client_name: user.client?.client_name,
      });

      try {
        const clientsList = await fetchClients();
        if (cancelled) return;
        const foundClient = clientsList.find((client) => client.client_id === clientId);
        setSelectedClient(foundClient ? hydrateClient(foundClient) : fallback);
      } catch (error) {
        if (!cancelled) setSelectedClient(fallback);
      }
    };

    loadClient();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- isStaffUser is derived from `user`, which is already a dependency.
  }, [user, selectedClient]);

  // For non-staff users, fall back to user.client synchronously so the first
  // render never sees clientId=null while user data is already available.
  const effectiveClient = selectedClient ?? (!isStaffUser() ? clientFromUser(user) : null);

  return (
    <>
      {hideLayout ? (
        // AUTH PAGES DIRECTLY WITHOUT LAYOUT
        <Routes>
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.AUTH_CALLBACK} element={<AzureCallback />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />

          {/* Redirect unknown routes to login */}
          <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
        </Routes>
      ) : (
        <ProtectedRoute>
          <DataCacheProvider currentClientId={effectiveClient?.client_id || null}>
            <Layout notificationsOpen={showNotifications} onToggleNotifications={() => setShowNotifications((prev) => !prev)} selectedClient={effectiveClient} onClientSelect={setSelectedClient} user={user}>
              {showNotifications ? (
                <NotificationsPage />
              ) : (
                <Routes>
                {/* HOME REDIRECT */}
                <Route path={ROUTES.ROOT} element={
                  <Navigate to={
                    isStaffUser()
                      ? ROUTES.SUPERADMIN_OVERVIEW
                      : getDefaultHomeRoute(user)
                  } replace />
                } />

                {/* ---------------------------------------------------------------------- */}
                {/*                       SUPERADMIN ROUTES (Only for superadmin)          */}
                {/* ---------------------------------------------------------------------- */}
                <Route path={ROUTES.SUPERADMIN_OVERVIEW} element={
                  <ProtectedRoute requireSuperadmin>
                    <RestrictedClientRoute client={effectiveClient}>
                      <Overview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                    </RestrictedClientRoute>
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.SUPERADMIN_SECURITY} element={
                  <ProtectedRoute requireSuperadmin>
                    <RestrictedClientRoute client={effectiveClient}>
                      <SecurityOverview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                    </RestrictedClientRoute>
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.SUPERADMIN_ASSETS} element={
                  <ProtectedRoute requireSuperadmin>
                    <RestrictedClientRoute client={effectiveClient}>
                      <AssetsOverview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                    </RestrictedClientRoute>
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.SUPERADMIN_NETWORK} element={
                  <ProtectedRoute requireSuperadmin>
                    <RestrictedClientRoute client={effectiveClient}>
                      <NetworkOverview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                    </RestrictedClientRoute>
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.SUPERADMIN_ENGAGEMENT} element={
                  <ProtectedRoute requireSuperadmin>
                    <EngagementPage clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </ProtectedRoute>
                } />
                <Route path={ROUTES.SUPERADMIN_ENGAGEMENT_RESELL} element={
                  <ProtectedRoute requireSuperadmin>
                    <EngagementPage clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} defaultTab="resell" />
                  </ProtectedRoute>
                } />

                {/* ---------------------------------------------------------------------- */}
                {/*                     CLIENT ROUTES (For all authenticated users)         */}
                {/* ---------------------------------------------------------------------- */}
                <Route path={ROUTES.CLIENT_OVERVIEW} element={
                  <RestrictedClientRoute client={effectiveClient}>
                    <Overview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </RestrictedClientRoute>
                } />
                <Route path={ROUTES.CLIENT_SECURITY} element={
                  <RestrictedClientRoute client={effectiveClient}>
                    <SecurityOverview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </RestrictedClientRoute>
                } />
                <Route path={ROUTES.CLIENT_ASSETS} element={
                  <RestrictedClientRoute client={effectiveClient}>
                    <AssetsOverview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </RestrictedClientRoute>
                } />
                <Route path={ROUTES.CLIENT_NETWORK} element={
                  <RestrictedClientRoute client={effectiveClient}>
                    <NetworkOverview clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </RestrictedClientRoute>
                } />
                <Route path={ROUTES.CLIENT_ENGAGEMENT} element={<EngagementPage clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />} />
                <Route path={ROUTES.CLIENT_ENGAGEMENT_RESELL} element={<EngagementPage clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} defaultTab="resell" />} />

                <Route path={ROUTES.SUPERADMIN_HELPDESK} element={
                  <RestrictedClientRoute client={effectiveClient}>
                    <HelpdeskPage clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </RestrictedClientRoute>
                } />
                <Route path={ROUTES.CLIENT_HELPDESK} element={
                  <RestrictedClientRoute client={effectiveClient}>
                    <HelpdeskPage clientId={effectiveClient?.client_id || null} selectedClient={effectiveClient} />
                  </RestrictedClientRoute>
                } />

                {/* CLIENT's special standalone page (kept unique) */}
                <Route path={ROUTES.CLIENT_DASHBOARD} element={<ClientOverview />} />

                {/* USER PROFILE */}
                <Route path={ROUTES.PROFILE} element={<UserProfilePage />} />
                </Routes>
              )}
            </Layout>
          </DataCacheProvider>
        </ProtectedRoute>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 ROOT ROUTER                                */
/* -------------------------------------------------------------------------- */

export default function RootApp() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DateFilterProvider>
          <AppWrapper />
        </DateFilterProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
